import { Modal, Text } from "@carbon6/c6-frontend-design";
import { useId, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { Keycap } from "@/components/keyboard";
import type { LabelCategory } from "@/api/labelCategory";
import {
  ACTION_SOURCE_OPTIONS,
  episodeQualityTagText,
  episodeQualityTagTip,
  episodeFailureLabel,
  episodeFailureTip,
} from "@/components/label";
import { HatModalHeader } from "@/components/modal";
import { ACCEPT_KEY, FAILURE_KEY, MODE_KEY } from "./episodeTagHotkeys";
import styles from "./EpisodeLabelGuideModal.module.css";

// 갈래에 없는 서버 태그를 모으는 묶음. 어휘 SoT 와 갈래 SoT 가 모두 서버다. 둘이 어긋나도
// 태그가 빠지면 안 된다. 운영자가 소속을 해제한 값이 여기에 들어간다.
const UNGROUPED_TITLE = "그 외";

// 갈래 카탈로그를 아직 받지 못했을 때의 묶음 이름. `그 외` 를 쓰지 않는다. 전부를 "그 외" 라고
// 부를 수 없다.
const ALL_TITLE = "결함";

// 태그가 아닌 액션 단축키. 칩에는 키캡이 표시되지만, 이동·저장 키는 이 표에만 적는다.
const ACTION_SHORTCUTS: readonly { keys: readonly string[]; description: string }[] = [
  { keys: ["↑", "↓"], description: "이전 / 다음 에피소드 (목록에 보이는 순서)" },
  {
    keys: [FAILURE_KEY.toUpperCase()],
    description: "실패 표시를 켜고 끕니다. 끄면 기본 성공으로 돌아갑니다",
  },
  { keys: ["X"], description: "이 에피소드를 Training에서 제외" },
  {
    keys: [ACCEPT_KEY.toUpperCase()],
    description:
      "결함 수용을 켜고 끕니다. 검증이 무효인 에피소드에서만 동작합니다. 채널 누락 에피소드는 수용할 수 없습니다",
  },
  { keys: ["K"], description: "표기 편집 열기 (키: 값)" },
  { keys: ["C"], description: "코멘트 입력으로 이동. 뷰 모드면 리뷰 모드로 바꿉니다" },
  {
    keys: ["Enter", "S"],
    description: "저장·검토 완료하고 다음 에피소드로. 수정 내용이 없어도 완료합니다",
  },
  { keys: ["Ctrl/⌘+Enter"], description: "코멘트 입력 안에서 저장·검토 완료" },
  { keys: ["Esc"], description: "코멘트 입력에서 나오기" },
  {
    keys: [MODE_KEY.toUpperCase()],
    description: "리뷰 모드 ↔ 뷰 모드 (뷰 모드에서도 듣습니다)",
  },
  { keys: ["?"], description: "이 가이드" },
];

export type EpisodeLabelGuideModalProps = {
  open: boolean;
  // 서버가 준 어휘·순서(`GET /v1/review-options`). **순번 키는 이 배열 위치가 정한다.**
  // 묶음은 읽기용 갈래일 뿐이다. 묶음 순서로 키캡을 정하면 칩과 어긋난다.
  qualityTags: readonly string[];
  // 결함 갈래(`GET /v1/label-categories?key=quality`). 제목·구분 기준·소속을 모두 여기서
  // 받는다. 이름과 경계는 배포 없이 바뀔 수 있으므로 DB 가 SoT 다. 받지 못했으면 빈 배열이다.
  // 그때는 갈래 없이 한 묶음으로 그린다.
  categories: readonly LabelCategory[];
  // 순번 → 키. 칩과 같은 표를 쓴다(`episodeTagHotkeys.ts` 가 소유).
  tagHotkeys: readonly string[];
  onClose: () => void;
  // 닫힘 모션이 끝난 뒤 1회 호출한다. 소비처가 언마운트한다(Studio 시트와 같은 방식).
  onClosed?: () => void;
};

/**
 * 에피소드 라벨링 가이드. 어휘의 뜻과 단축키를 한 화면에 보여 준다.
 *
 * 칩 툴팁은 칩 하나만 설명하므로 **대조**를 담지 못한다. "작업 도중 종료와 시간 초과가 어떻게
 * 다른가" 같은 질문은 둘을 나란히 놓아야 답할 수 있다. 그래서 갈래별로 묶고, 각 묶음 머리에
 * 구분 기준을 적는다. 묶음(`display_name`)·기준(`description`)·소속(`values`)은 **서버가
 * 소유한다**(`label_categories`). 경계가 바뀔 수 있어 배포 없이 고쳐야 하고, 필터 드롭다운도 같은
 * 갈래를 쓴다. 이 컴포넌트는 순수하게 두고 props 로만 받는다.
 *
 * `?` 로 열고 mount-on-open 으로 그린다. 닫힘 모션이 끝나면 언마운트하므로 평소에는 트리를
 * 차지하지 않는다. c6 에 대응 컴포넌트가 없으므로 앱이 구현을 소유한다.
 */
export function EpisodeLabelGuideModal({
  open,
  qualityTags,
  categories,
  tagHotkeys,
  onClose,
  onClosed,
}: EpisodeLabelGuideModalProps): ReactNode {
  const titleId = useId();
  const groups = groupServerTags(qualityTags, categories);

  return (
    <Modal open={open} onClose={onClose} onClosed={onClosed} size="lg" aria-labelledby={titleId}>
      <HatModalHeader title="에피소드 라벨링 가이드" titleId={titleId} close />
      <div className={styles.body}>
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.lede}>
          리뷰의 <b>결과</b>와 <b>결함</b>은 서로 독립입니다. 실패한 에피소드에 결함 태그가 없을
          수도 있습니다. 결함 태그가 비어 있는 것은 &ldquo;결함 없음&rdquo;이 아니라 &ldquo;결함을
          적지 않았다&rdquo;는 뜻이라, 실패 여부는 결과 축만이 답합니다.
        </Text>

        <section className={styles.group}>
          <h3 className={styles.groupTitle}>결과</h3>
          <p className={styles.groupHint}>
            작업을 완수하지 못한 에피소드에 실패를 표시합니다. 표시하지 않은 에피소드는 기본
            성공입니다. 실패로 두어도 Training에서 자동으로 빠지지는 않습니다. 실패 에피소드를 실패
            인식 Training에 쓰는 것이 정상입니다. 빼려면 제외를 따로 켭니다.
          </p>
          <dl className={styles.rows}>
            <div className={styles.row}>
              <dt className={styles.keys}>
                <Keycap>{FAILURE_KEY.toUpperCase()}</Keycap>
                <span className={styles.tagName}>{episodeFailureLabel(true)}</span>
              </dt>
              <dd className={styles.description}>{episodeFailureTip(true)}</dd>
            </div>
            <div className={styles.row}>
              <dt className={styles.keys}>
                <span className={styles.tagName}>{episodeFailureLabel(false)}</span>
              </dt>
              <dd className={styles.description}>{episodeFailureTip(false)}</dd>
            </div>
          </dl>
        </section>

        <div className={styles.grid}>
          {groups.map((group) => (
            <section key={group.title} className={styles.group}>
              <h3 className={styles.groupTitle}>{group.title}</h3>
              {group.hint ? <p className={styles.groupHint}>{group.hint}</p> : null}
              <dl className={styles.rows}>
                {group.tags.map(({ tag, index }) => (
                  <div key={tag} className={styles.row}>
                    <dt className={styles.keys}>
                      {index < tagHotkeys.length ? (
                        <Keycap>{tagHotkeys[index]?.toUpperCase()}</Keycap>
                      ) : (
                        <span className={styles.noKey}>—</span>
                      )}
                      <span className={styles.tagName}>{episodeQualityTagText(tag)}</span>
                    </dt>
                    <dd className={styles.description}>{episodeQualityTagTip(tag) ?? ""}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <section className={styles.group}>
          <h3 className={styles.groupTitle}>행동 출처</h3>
          <p className={styles.groupHint}>
            녹화된 Action을 누가 만들었는지 나타냅니다. 결함이나 실패 판정과 독립입니다. 리뷰로
            수정하지 않으며, 진단에서 기록된 구간을 확인합니다. 개입처럼 행동 출처가 섞여 있어도
            결함 태그를 붙이지 않습니다. 개입 구간은 행동 출처 구간으로 확인합니다.
          </p>
          <dl className={styles.rows}>
            {ACTION_SOURCE_OPTIONS.map((option) => (
              <div key={option.value} className={styles.row}>
                <dt className={styles.keys}>
                  <span className={styles.tagName}>{option.label}</span>
                </dt>
                <dd className={styles.description}>{option.description}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={styles.group}>
          <h3 className={styles.groupTitle}>단축키</h3>
          <dl className={`${styles.rows} ${styles.shortcutRows}`}>
            {ACTION_SHORTCUTS.map((shortcut) => (
              <div key={shortcut.description} className={styles.row}>
                <dt className={styles.keys}>
                  {shortcut.keys.map((key) => (
                    <Keycap key={key}>{key}</Keycap>
                  ))}
                </dt>
                <dd className={styles.description}>{shortcut.description}</dd>
              </div>
            ))}
          </dl>
          <p className={styles.groupHint}>
            입력 칸 안에서는 단축키를 가로채지 않습니다. 뷰 모드에서는 실패·제외·저장·코멘트·모드만
            듣고 나머지 편집 단축키는 꺼집니다.
          </p>
        </section>
      </div>
    </Modal>
  );
}

type GuideGroup = {
  title: string;
  hint?: string;
  tags: readonly { tag: string; index: number }[];
};

/**
 * 서버 어휘를 읽기용 갈래로 묶는다. 각 태그에 **서버 배열에서의 위치**를 함께 담는다. 그래서
 * 키캡이 칩과 같은 키를 표시한다. 묶음에 없는 태그는 순서를 유지한 채 `그 외`에 넣어 빠지지 않게 한다.
 */
function groupServerTags(
  qualityTags: readonly string[],
  categories: readonly LabelCategory[],
): readonly GuideGroup[] {
  const indexByTag = new Map(qualityTags.map((tag, index) => [tag, index] as const));

  // 갈래를 받지 못했으면 전체를 한 묶음으로 서버 순서대로 둔다. 키캡·문구·툴팁은 그대로다.
  // 그래서 가이드의 주 역할(어휘의 뜻)은 유지되고, 대조 기준만 빠진다.
  if (categories.length === 0) {
    const all = qualityTags.map((tag, index) => ({ tag, index }));
    return all.length > 0 ? [{ title: ALL_TITLE, tags: all }] : [];
  }

  const grouped = new Set<string>();
  const groups: GuideGroup[] = [];
  // 갈래 순서는 서버가 `(key, sort_order, code)` 로 정해서 준다. 여기서 다시 정렬하지 않는다.
  for (const category of categories) {
    const tags = category.values
      // 서버가 어휘로 주지 않은 값은 그리지 않는다. 갈래 소속과 어휘가 어긋나도 화면에는 실제로
      // 붙일 수 있는 태그만 표시한다.
      .filter((tag) => indexByTag.has(tag))
      .map((tag) => {
        grouped.add(tag);
        return { tag, index: indexByTag.get(tag) ?? 0 };
      });
    if (tags.length > 0) {
      groups.push({
        title: category.display_name,
        ...(category.description === null ? {} : { hint: category.description }),
        tags,
      });
    }
  }

  const rest = qualityTags
    .map((tag, index) => ({ tag, index }))
    .filter(({ tag }) => !grouped.has(tag));
  if (rest.length > 0) {
    groups.push({ title: UNGROUPED_TITLE, tags: rest });
  }
  return groups;
}
